import { IconAlertTriangle } from '@tabler/icons-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
} from '../../atoms/alert-dialog'
import { FC, useCallback, useState } from 'react'
import Typography from 'src/components/atoms/Typography'
import { AlertDialogTitle } from '@radix-ui/react-alert-dialog'
import { useCancelSubscription, useGetSubscription } from 'src/enviroment/api/services/subscriptionsApi'
import { enqueueSnackbar } from 'notistack'
import { format } from 'date-fns'

interface Props {
  onCancel?: () => void
}

const CancelSubscriptionDialog: FC<Props> = ({ onCancel }) => {
  const [opened, setOpened] = useState<boolean>(false)
  const { data: subscription } = useGetSubscription()
  const { mutateAsync: cancelSubscription, isLoading: isCancelling } = useCancelSubscription()

  const handleClose = useCallback(() => {
    setOpened(false)
    onCancel?.()
  }, [onCancel])

  const handleCancellation = useCallback(async () => {
    try {
      await cancelSubscription()
      enqueueSnackbar('Your subscription will be cancelled at the end of the billing period', { variant: 'success' })
      handleClose()
    } catch {
      enqueueSnackbar('An error occurred while cancelling the subscription', { variant: 'error' })
    }
  }, [cancelSubscription, handleClose])

  // If scheduled for cancellation - show warning
  if (subscription?.cancelAtPeriodEnd && subscription?.currentPeriodEnd) {
    return (
      <div className="rounded-lg border border-yellow-600 bg-yellow-900/20 p-4">
        <div className="flex gap-3">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-yellow-500/20">
            <IconAlertTriangle className="h-4 w-4 text-yellow-500" />
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="base" className="font-semibold text-yellow-500">
              Cancellation Scheduled
            </Typography>
            <Typography variant="sm" className="text-gray-300">
              Your Pro plan remains active until{' '}
              <span className="font-semibold text-light-green-50">
                {format(new Date(subscription.currentPeriodEnd), 'MMMM dd, yyyy')}
              </span>
              . After this date, you&apos;ll be moved to the Free plan.
            </Typography>
          </div>
        </div>
      </div>
    )
  }

  return (
    <AlertDialog open={opened} onOpenChange={setOpened}>
      <AlertDialogTrigger className="w-full sm:w-auto">
        <Typography variant="base" className="text-red-500">
          Cancel Subscription
        </Typography>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogTitle>
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/20">
              <IconAlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <Typography variant="xl" className="text-center text-light-green-50">
                Cancel Subscription
              </Typography>
              <Typography variant="base" className="text-center text-gray-400">
                Are you sure you want to cancel your subscription?
              </Typography>
            </div>
            {subscription?.currentPeriodEnd && (
              <div className="w-full rounded-lg bg-gray-800 px-4 py-3 text-center">
                <Typography variant="sm" className="text-gray-400">
                  You&apos;ll keep Pro access until
                </Typography>
                <Typography variant="base" className="mt-1 font-semibold text-light-green-50">
                  {format(new Date(subscription.currentPeriodEnd), 'MMMM dd, yyyy')}
                </Typography>
              </div>
            )}
          </div>
        </AlertDialogTitle>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isCancelling} onClick={handleClose}>
            Keep Subscription
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isCancelling}
            onClick={handleCancellation}
            className="bg-red-600 hover:bg-red-700"
          >
            {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default CancelSubscriptionDialog
